// Automatic Error Fixer System
export interface ErrorPattern {
  pattern: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'navigation' | 'component' | 'api' | 'data' | 'auth' | 'ai';
  fixStrategy: 'retry' | 'fallback' | 'redirect' | 'reload' | 'ignore';
  autoFix: boolean;
  userMessage: string;
  technicalDetails: string;
}

export interface AutoFixResult {
  success: boolean;
  action: string;
  message: string;
  requiresUserAction: boolean;
}

class AutoErrorFixer {
  private errorPatterns: ErrorPattern[] = [];
  private fixHistory: Array<{ timestamp: string; pattern: string; success: boolean }> = [];

  constructor() {
    this.initializeErrorPatterns();
  }

  private initializeErrorPatterns(): void {
    this.errorPatterns = [
      // AI Insights specific patterns
      {
        pattern: 'AI Insights button',
        severity: 'high',
        category: 'ai',
        fixStrategy: 'fallback',
        autoFix: true,
        userMessage: 'AI Insights temporarily unavailable. Using safe navigation.',
        technicalDetails: 'AI component failed to load, using fallback navigation'
      },
      {
        pattern: 'Failed to resolve import',
        severity: 'high',
        category: 'component',
        fixStrategy: 'reload',
        autoFix: false,
        userMessage: 'Component loading issue detected. Please refresh the page.',
        technicalDetails: 'Import resolution failed, component may be missing'
      },
      {
        pattern: 'Maximum update depth exceeded',
        severity: 'critical',
        category: 'component',
        fixStrategy: 'reload',
        autoFix: true,
        userMessage: 'Infinite loop detected. Refreshing page to resolve.',
        technicalDetails: 'React infinite re-render loop detected'
      },
      {
        pattern: 'Cannot read property',
        severity: 'medium',
        category: 'data',
        fixStrategy: 'fallback',
        autoFix: true,
        userMessage: 'Data loading issue. Using cached data.',
        technicalDetails: 'Property access on undefined/null object'
      },
      {
        pattern: 'window.location.href',
        severity: 'medium',
        category: 'navigation',
        fixStrategy: 'redirect',
        autoFix: true,
        userMessage: 'Navigation issue. Using safe redirect.',
        technicalDetails: 'Direct location change detected, using React Router'
      },
      {
        pattern: 'CustomEvent',
        severity: 'low',
        category: 'component',
        fixStrategy: 'retry',
        autoFix: true,
        userMessage: 'Component communication issue. Retrying...',
        technicalDetails: 'Custom event dispatch failed'
      }
    ];
  }

  public analyzeError(error: Error, context: Record<string, any> = {}): ErrorPattern | null {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';

    return this.errorPatterns.find(pattern => {
      const patternLower = pattern.pattern.toLowerCase();
      return message.includes(patternLower) || stack.includes(patternLower);
    }) || null;
  }

  public async attemptAutoFix(error: Error, context: Record<string, any> = {}): Promise<AutoFixResult> {
    const pattern = this.analyzeError(error, context);
    
    if (!pattern || !pattern.autoFix) {
      return {
        success: false,
        action: 'none',
        message: 'No automatic fix available for this error',
        requiresUserAction: true
      };
    }

    try {
      let action = '';
      let success = false;

      switch (pattern.fixStrategy) {
        case 'retry':
          action = 'retry';
          success = await this.retryOperation(context);
          break;

        case 'fallback':
          action = 'fallback';
          success = await this.useFallback(pattern, context);
          break;

        case 'redirect':
          action = 'redirect';
          success = await this.safeRedirect(context);
          break;

        case 'reload':
          action = 'reload';
          success = await this.safeReload();
          break;

        case 'ignore':
          action = 'ignore';
          success = true;
          break;

        default:
          action = 'none';
          success = false;
      }

      // Log the fix attempt
      this.fixHistory.push({
        timestamp: new Date().toISOString(),
        pattern: pattern.pattern,
        success
      });

      return {
        success,
        action,
        message: success ? pattern.userMessage : 'Auto-fix failed',
        requiresUserAction: !success
      };

    } catch (fixError) {
      console.error('Auto-fix attempt failed:', fixError);
      return {
        success: false,
        action: 'failed',
        message: 'Auto-fix attempt failed',
        requiresUserAction: true
      };
    }
  }

  private async retryOperation(context: Record<string, any>): Promise<boolean> {
    try {
      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Retry the operation based on context
      if (context.operation === 'ai_navigation') {
        // Retry AI navigation with safe fallback
        return this.safeAINavigation();
      }
      
      return true;
    } catch (error) {
      return false;
    }
  }

  private async useFallback(pattern: ErrorPattern, context: Record<string, any>): Promise<boolean> {
    try {
      if (pattern.category === 'ai') {
        // Use safe AI navigation fallback
        return this.safeAINavigation();
      }
      
      if (pattern.category === 'data') {
        // Use cached data or default values
        return this.useCachedData(context);
      }
      
      return true;
    } catch (error) {
      return false;
    }
  }

  private async safeRedirect(context: Record<string, any>): Promise<boolean> {
    try {
      // Use React Router navigation instead of window.location
      if (window.history && window.history.pushState) {
        const targetPath = context.targetPath || '/';
        window.history.pushState({}, '', targetPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  private async safeReload(): Promise<boolean> {
    try {
      // Show user-friendly reload message
      this.showNotification('Refreshing page to resolve the issue...', 'info');
      
      // Small delay to show message
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      window.location.reload();
      return true;
    } catch (error) {
      return false;
    }
  }

  private async safeAINavigation(): Promise<boolean> {
    try {
      // Try to navigate to AI coach page safely
      if (window.history && window.history.pushState) {
        window.history.pushState({}, '', '/ai-coach');
        window.dispatchEvent(new PopStateEvent('popstate'));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  private async useCachedData(context: Record<string, any>): Promise<boolean> {
    try {
      // Try to use cached data from localStorage
      const cachedData = localStorage.getItem('zippyhealth_cached_data');
      if (cachedData) {
        // Parse and use cached data
        const data = JSON.parse(cachedData);
        // Dispatch event to notify components of cached data
        window.dispatchEvent(new CustomEvent('useCachedData', { detail: data }));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  private showNotification(message: string, type: 'info' | 'warning' | 'error' = 'info'): void {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 ${
      type === 'info' ? 'bg-blue-500 text-white' :
      type === 'warning' ? 'bg-yellow-500 text-white' :
      'bg-red-500 text-white'
    }`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  }

  public getFixHistory(): Array<{ timestamp: string; pattern: string; success: boolean }> {
    return [...this.fixHistory];
  }

  public getFixStats(): {
    totalAttempts: number;
    successfulFixes: number;
    successRate: number;
    recentPatterns: string[];
  } {
    const totalAttempts = this.fixHistory.length;
    const successfulFixes = this.fixHistory.filter(fix => fix.success).length;
    const successRate = totalAttempts > 0 ? (successfulFixes / totalAttempts) * 100 : 0;
    const recentPatterns = this.fixHistory
      .slice(-10)
      .map(fix => fix.pattern)
      .filter((pattern, index, array) => array.indexOf(pattern) === index);

    return {
      totalAttempts,
      successfulFixes,
      successRate,
      recentPatterns
    };
  }
}

// Global auto-fixer instance
export const autoErrorFixer = new AutoErrorFixer(); 